import { useEffect, useState } from 'react';
import _ from 'lodash';
import styled from 'styled-components';
import Switch from '@material-ui/core/Switch';
import { Slider } from '@material-ui/core';
import SnackBarMessage from 'components/Message';
import { HyperLink } from 'components/base/HyperLink';
import { Flex, Padding, Separator } from 'components/base/layout-components';
import Text from 'components/base/Text';
import InfoBox from 'components/InfoBox';
import { TRACKING } from 'common/constants/constants';
import { LINE_HEIGHT, SPACES } from 'common/constants/sizes';
import { pluralize } from 'common/utils/utils';
import * as TrackingService from 'common/services/tracking.service';
import { useNotificationPrediction } from 'views/root/views/home/views/settings/views/Notifications/hooks/useNotificationPrediction';
import {
  getNotificationThresholds,
  overPrsThreshholdCheck,
  PRS_THRESHHOLD
} from 'views/root/views/home/views/settings/views/Notifications/notifications.service';
import { LBButton } from '@linearb/buzz';

const SwitchWrapper = styled(Flex)`
  width: 4rem;
`;

export const NotificationCustomizationModal = ({
  toggleSnackBar,
  selectedTeamId,
  id,
  name,
  notifications,
  setNotifications,
  closeModal,
  onSaveSetting,
  withEstimation = true,
  inModalDescription,
  NOTIFICATION_CONFIG,
  NOTIFICATIONS_BUILDER,
  prMerged,
  predictionCount,
  setPredictionCount
}) => {
  const [notification, setNotification] = useState(
    notifications.find(n => n.id === id)
  );
  const [restore, setRestore] = useState(false);

  const { schema, defaultValue } = notification || {};

  const notificationThresholds = getNotificationThresholds(notification);

  const { predictionsCount, getPredictions } = useNotificationPrediction({
    selectedTeamId,
    notifications: [notification],
    NOTIFICATIONS_BUILDER,
    notificationThresholds,
    prMerged
  });

  useEffect(() => {
    if (restore) getPredictions();
  }, [restore]);

  const thresholds = NOTIFICATION_CONFIG.thresholds(notificationThresholds);
  const hasOptionalThresholds = _.flatMap(
    Object.values(_.get(thresholds, '[0]', [])),
    item => Object.keys(item)
  ).includes('optional');

  const isNotificationEnabled = _.get(notification, 'values', {})[
    'violation_enabled'
  ];

  const isHeadsUpEnabled = _.get(notification, 'values', {})['warning_enabled'];

  const showPredictionCount = () => {
    const isDouble = !!isHeadsUpEnabled && !!isNotificationEnabled;
    const estimateNumber = _.get(predictionsCount, '[0].count', 0);
    const count = _.round(isDouble ? estimateNumber * 2 : estimateNumber);
    return count > 0 ? (
      <b> {pluralize(count, 'notification')} per day</b>
    ) : (
      <b> less than 1 notification per day</b>
    );
  };

  const doSlide = key => (e, newValue) => {
    const _notification = {
      ...notification,
      values: { ...notification.values, [key]: newValue }
    };
    setRestore(false);
    setNotification(_notification);
  };

  const doSave = async () => {
    try {
      TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
        page: TRACKING.PAGES.SETTINGS,
        page_tab: TRACKING.PAGE_TABS.NOTIFICATIONS,
        click_type: TRACKING.CLICK.SAVE,
        name: notification.name,
        values: notificationThresholds
      });

      const updatedNotifications = notifications.map(n =>
        n.id === id
          ? {
              ...n,
              values: { ...notification.values }
            }
          : n
      );
      const updatedNotification = _.find(
        updatedNotifications,
        n => n.id === id
      );

      setNotifications(updatedNotifications);
      await onSaveSetting(
        notification.notificationKey,
        {
          ...updatedNotification.values
        },
        'team',
        selectedTeamId
      );

      toggleSnackBar({
        message: <SnackBarMessage message='Settings updated successfully' />,
        variant: 'success'
      });
    } catch (e) {
      toggleSnackBar({
        message: (
          <SnackBarMessage message='Failed to update settings, please try again later' />
        ),
        variant: 'error'
      });
      return false;
    }
    const newPredictionCount = {
      ...predictionCount,
      [notification.notificationKey]: predictionsCount[0] || 0
    };
    setPredictionCount(newPredictionCount);

    closeModal();
  };

  const restoreDefault = () => {
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.SETTINGS,
      page_tab: TRACKING.PAGE_TABS.NOTIFICATIONS,
      click_type: TRACKING.CLICK.RESTORE_DEFAULTS,
      name: notification.name
    });

    const _notification = {
      ...notification,
      values: defaultValue
    };
    setRestore(true);
    setNotification(_notification);
  };

  const isOverPrsThreshhold = overPrsThreshholdCheck(prMerged, PRS_THRESHHOLD);
  return (
    <Flex direction='column' padding='medium'>
      <Flex pb='medium' items='center'>
        <Text size='x-large'>{name}</Text>
      </Flex>
      {inModalDescription}
      <Text color='shuttleGray' size='medium' pb={SPACES['x-medium']}>
        Notify me when:
      </Text>
      {Object.entries(notificationThresholds).map(([key, value], index) => {
        const isLast =
          index === Object.entries(notificationThresholds).length - 1;
        if (!thresholds || !thresholds[0] || !thresholds[0][key]) return false;
        const {
          children,
          minMaxFormatter,
          sliderSteps,
          optional = {}
        } = thresholds[0][key];
        return (
          <div key={index}>
            <Flex pb='small'>
              {!_.isEmpty(optional) && (
                <SwitchWrapper>
                  <Switch
                    size='small'
                    onChange={event => {}}
                    //checked={}
                  />
                </SwitchWrapper>
              )}
              <Flex direction='column'>{children}</Flex>
            </Flex>
            <Flex items='center'>
              {hasOptionalThresholds && <SwitchWrapper />}
              <Flex items='center' grow='1'>
                <Text
                  data-testid='min'
                  size='small'
                  color='shuttleGray'
                  width='3rem'
                  pr={SPACES.small}
                >
                  {minMaxFormatter
                    ? minMaxFormatter(schema[key].min)
                    : schema[key].min}
                </Text>
                <Slider
                  min={schema[key].min}
                  max={schema[key].max}
                  value={notificationThresholds[key]}
                  onChange={doSlide(key)}
                  onChangeCommitted={getPredictions}
                  aria-labelledby={
                    sliderSteps ? 'discrete-slider' : 'continuous-slider'
                  }
                  {...(sliderSteps ? { step: sliderSteps } : {})}
                />
                <Text
                  data-testid='max'
                  size='small'
                  color='shuttleGray'
                  width='3rem'
                  align='end'
                  pl={SPACES.small}
                >
                  {minMaxFormatter
                    ? minMaxFormatter(schema[key].max)
                    : schema[key].max}
                </Text>
              </Flex>
            </Flex>

            {!isLast && (
              <Separator
                direction='horizontal'
                width='100%'
                height='1px'
                margin='2rem 0'
              />
            )}
          </div>
        );
      })}

      {withEstimation && isOverPrsThreshhold ? (
        <InfoBox>
          <Text
            data-testid='prediction-count'
            size='small'
            color='shuttleGray'
            lineHeight={LINE_HEIGHT.small}
          >
            Based on recent activity we estimate that with these settings you
            will receive:
            {showPredictionCount()}
          </Text>
        </InfoBox>
      ) : null}

      <Flex items='center' justify='flex-end' pt='x-medium'>
        <Padding r='medium'>
          <HyperLink
            size='medium'
            onClick={() => {
              restoreDefault();
            }}
          >
            Restore default
          </HyperLink>
        </Padding>

        <LBButton
          data-testid='save-btn'
          onClick={() => {
            doSave();
          }}
        >
          Save
        </LBButton>
      </Flex>
    </Flex>
  );
};

export default NotificationCustomizationModal;
